import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  BackHandler,
  Dimensions,
  Keyboard,
  PixelRatio,
  Platform,
  TouchableOpacity,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import {hdp as staticHdp, isWeb} from '../../utils/responsive';
import useKeyboardStatus from '../../utils/hooks/useKeyboardStatus';
import Icon from '../../components/icon';
import {tr} from '../../translations';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import TextInput from '../../components/textInput';
import {closeCountryPickerModal, searchAndFilterCountries} from './actions';
import {CountryPickerModalProps} from '../../interfaces/connected-components/countryPickerModal';
import {Country} from '../../interfaces/api/auth/login';
import CountryListItem from '../../components/countryListItem';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const screenHeight = Dimensions.get('screen').height;
const modalHeight = staticHdp(90);
const separatorHeight = 28 / PixelRatio.get();

export const CountryListHeaderHeight = 252 / PixelRatio.get();
export const CountryListItemHeight = Platform.OS === 'web' ? 40 : 118 / PixelRatio.get();

// Web-specific modal styles
const WebOverlayContainer = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10001;
`;

const WebModalContainer = styled.View<{responsiveWidth: number}>`
  width: ${({responsiveWidth}) => responsiveWidth}px;
  max-height: 80vh;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 8px;
  overflow: hidden;
  shadow-color: #000;
  shadow-offset: 0px 5px;
  shadow-opacity: 0.2;
  shadow-radius: 15px;
  elevation: 10;
  ${isWeb ? 'box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);' : ''}
`;

const WebHeaderRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom-width: 2px;
  border-bottom-color: ${({theme}) => theme.border + '33'};
  background-color: ${({theme}) => theme.homeBackground};
`;

const WebCloseButton = styled.TouchableOpacity`
  padding: 8px;
`;

// Mobile-specific styles remain unchanged
const ModalView = styled.View`
  flex: 1;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
`;
const Overlay = styled(Animated.View)`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
`;
const ContentContainer = styled.View<{
  responsiveWidth: number;
  responsiveHeight: number;
}>`
  border-radius: 16px;
  overflow: hidden;
  background-color: ${({theme}) => theme.homeBackground};
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 4;
  width: ${({responsiveWidth}) => responsiveWidth}px;
  height: ${({responsiveHeight}) => responsiveHeight}px;
  align-self: center;
  margin-top: auto;
  z-index: 10001;
`;
const BackIconContainer = styled.View`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  margin-right: 5%;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 4;
`;
const Separator = styled.View`
  height: ${separatorHeight}px;
`;
const HeaderContainer = styled(Animated.View)`
  position: absolute;
  top: 0;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 4%;
  background-color: ${({theme}) => theme.homeBackground};
  height: ${CountryListHeaderHeight}px;
  z-index: 999;
`;

const CountryPickerModal = ({
  countryPickerModal,
  auth,
}: CountryPickerModalProps) => {
  const isKeyboardOpen = useKeyboardStatus();
  const theme = useTheme() as Theme;
  const {getByScreenSize, hdp, wdp, isMobile} = useResponsiveDimensions();
  const {
    isVisible,
    onSelectionChange,
    currentValue,
    filteredCountryIds,
    withSearch,
  } = countryPickerModal || {};
  const {countries: countriesList} = auth?.config || {};

  const [modalVisible, setModalVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [searchCountries, setSearchCountries] = useState<Country[]>([]);

  // Animation values
  const overlayOpacity = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scale = useSharedValue(0);
  const duration = isVisible ? 700 : 800;
  const headerAnimation = useSharedValue(1);
  const previousScrollY = useSharedValue(1);
  const flatListRef = useRef<Animated.FlatList<any>>(null);

  // Optimized scroll handler
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      const currentScrollY = Math.abs(event.contentOffset.y);
      headerAnimation.value = withTiming(
        event.contentOffset.y < CountryListHeaderHeight ||
          (currentScrollY > previousScrollY.value
            ? currentScrollY - previousScrollY.value
            : previousScrollY.value - currentScrollY) <
            CountryListItemHeight / 2 ||
          currentScrollY < previousScrollY.value
          ? 1
          : 0,
      );
    },
    onBeginDrag: event => {
      previousScrollY.value = Math.abs(event.contentOffset.y);
    },
  });

  // Memoized header style for performance
  const headerStyle = useAnimatedStyle(() => {
    return {
      opacity: headerAnimation.value,
      transform: [
        {
          translateY: interpolate(
            headerAnimation.value,
            [0, 1],
            [-CountryListHeaderHeight, 0],
          ),
        },
      ],
    };
  }, []);

  const toggleModalAnimation = () => {
    overlayOpacity.value = withTiming(isVisible ? 1 : 0, {
      duration,
      easing: Easing.out(Easing.exp),
    });
    translateY.value = withTiming(isVisible ? 1 : 0, {
      duration,
      easing: Easing.out(Easing.exp),
    });
    scale.value = withTiming(isVisible ? 1 : 0, {
      duration,
      easing: Easing.out(Easing.exp),
    });
  };

  useEffect(() => {
    toggleModalAnimation();
    if (isVisible) {
      setModalVisible(true);
      initPicker(
        filteredCountryIds && filteredCountryIds.length > 0 ? false : true,
      );
    } else {
      setTimeout(() => {
        setModalVisible(false);
        initPicker();
        flatListRef?.current?.scrollToOffset({offset: 0});
      }, duration);
    }
  }, [isVisible]);

  useEffect(() => {
    handleSearch();
  }, [search]);

  const initPicker = useCallback(
    (refresh: boolean = true) => {
      const results = searchAndFilterCountries(
        '',
        refresh ? null : filteredCountryIds,
      );
      setSearchCountries(results);
      setCountries(results);
      setSearch('');
    },
    [filteredCountryIds],
  );

  // Handle back button press
  const handleBackPress = useCallback(() => {
    if (isVisible) {
      closeCountryPickerModal();
      return true;
    }
    return false;
  }, [isVisible]);

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        subscription.remove();
      };
    }
  }, [handleBackPress]);

  const handleSearch = useCallback(() => {
    if (search.length === 0) {
      setSearchCountries(countries);
      return;
    }
    const results = searchAndFilterCountries(search, null, countries);
    setSearchCountries(results);
  }, [search, countries]);

  const submit = (item: Country) => {
    onSelectionChange && onSelectionChange(item);
    closeCountryPickerModal();
  };

  const overlayStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: `rgba(0, 0, 0, ${overlayOpacity.value * 0.3})`,
    };
  }, []);

  const modalStyle = useAnimatedStyle(() => {
    const translate = interpolate(
      translateY.value,
      [0, 1],
      [screenHeight, screenHeight - modalHeight],
    );
    const scaleValue = interpolate(scale.value, [0, 1], [0.8, 1]);
    return {
      transform: [{translateY: translate}, {scale: scaleValue}],
    };
  }, []);

  const _renderItem = ({item}: {item: Country}) => {
    return (
      <CountryListItem
        item={item}
        onPress={submit}
        currentValue={currentValue}
      />
    );
  };

  const _renderHeader = useCallback(() => {
    const isSearch = (search || '').trim().length > 0;
    return (
      <HeaderContainer style={headerStyle} key={isVisible + ''}>
        <BackIconContainer>
          <Icon
            name={'x'}
            color={theme.text.dark}
            size={theme.text.s55}
            role={'button'}
            onPress={closeCountryPickerModal}
          />
        </BackIconContainer>
        {withSearch && (
          <TextInput
            value={search}
            onChangeText={text => setSearch(text)}
            placeholder={tr('countryPickerModal.searchPlaceholder')}
            showRightIcon
            rightIconName={isSearch ? 'search-off' : 'search'}
            rightIconType={'MaterialIcons'}
            onRightIconPress={isSearch ? () => setSearch('') : undefined}
            containerStyle={{
              flex: 1,
              alignSelf: 'center',
            }}
          />
        )}
      </HeaderContainer>
    );
  }, [search, withSearch, isVisible]);

  const _renderSeparator = useCallback(() => {
    return <Separator />;
  }, []);

  // Web-specific render function
  const renderWebContent = () => {
    return (
      <Animated.FlatList
        ref={flatListRef}
        onMomentumScrollBegin={() => isKeyboardOpen && Keyboard.dismiss()}
        data={searchCountries}
        keyExtractor={(item, index) => (item.id || index) + ''}
        renderItem={_renderItem}
        ItemSeparatorComponent={_renderSeparator}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: hdp(5),
          paddingTop: hdp(2),
        }}
        //@ts-ignore
        style={{
          maxHeight: '70vh',
          minHeight: '60vh',
        }}
        keyboardShouldPersistTaps={'handled'}
        automaticallyAdjustContentInsets={false}
        scrollEventThrottle={16}
        getItemLayout={(_data, index) => ({
          length: CountryListItemHeight,
          offset: CountryListItemHeight * index,
          index,
        })}
        initialNumToRender={15}
      />
    );
  };

  // Web-specific render
  if (isWeb && !isMobile) {
    if (!modalVisible) return null;
    return (
      <WebOverlayContainer>
        <Overlay
          onPointerDown={() => {
            isKeyboardOpen ? Keyboard.dismiss() : closeCountryPickerModal();
          }}
        />
        <WebModalContainer responsiveWidth={getByScreenSize(wdp(95), wdp(60))}>
          <WebHeaderRow>
            <TextInput
              value={search}
              onChangeText={text => setSearch(text)}
              placeholder={tr('countryPickerModal.searchPlaceholder')}
              showRightIcon
              rightIconName={search ? 'search-off' : 'search'}
              rightIconType={'MaterialIcons'}
              rightIconSize={theme.text.s6}
              onRightIconPress={search ? () => setSearch('') : undefined}
              containerStyle={{
                flex: 1,
                alignSelf: 'center',
                marginRight: 10,
              }}
              inputContainerStyle={{
                height: 40,
                minHeight: 40,
                borderRadius: 8,
              }}
              style={{
                paddingTop: 8,
                paddingBottom: 8,
                fontSize: theme.text.s7,
              }}
            />
            <WebCloseButton onPress={closeCountryPickerModal}>
              <Icon
                name={'close'}
                size={theme.text.s6}
                color={theme.secondary + 'AA'}
              />
            </WebCloseButton>
          </WebHeaderRow>
          {renderWebContent()}
        </WebModalContainer>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
          onPress={closeCountryPickerModal}
          activeOpacity={1}
        />
      </WebOverlayContainer>
    );
  }

  if (isWeb && !modalVisible) return null;
  // Mobile render remains unchanged
  return (
    <ModalView pointerEvents={modalVisible ? 'auto' : 'none'}>
      <Overlay
        onTouchStart={() => {
          isKeyboardOpen ? Keyboard.dismiss() : closeCountryPickerModal();
        }}
        style={overlayStyle}
      />
      <Animated.View style={modalStyle}>
        <ContentContainer responsiveWidth={wdp(95)} responsiveHeight={hdp(90)}>
          <Animated.FlatList
            ref={flatListRef}
            onMomentumScrollBegin={() => isKeyboardOpen && Keyboard.dismiss()}
            data={searchCountries}
            keyExtractor={item => String(item.id)}
            renderItem={_renderItem}
            ItemSeparatorComponent={_renderSeparator}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: hdp(10),
              paddingTop: CountryListHeaderHeight,
            }}
            onScroll={scrollHandler}
            keyboardShouldPersistTaps={'handled'}
            automaticallyAdjustContentInsets={false}
            scrollEventThrottle={5}
            getItemLayout={(_data, index) => ({
              length: CountryListItemHeight,
              offset: CountryListItemHeight * index,
              index,
            })}
            initialNumToRender={isWeb ? (countriesList || [])?.length : 15}
          />
          {_renderHeader()}
        </ContentContainer>
      </Animated.View>
    </ModalView>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    countryPickerModal: state.countryPickerModal,
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(CountryPickerModal);
